import React, { useEffect, useState } from 'react';
import { useSettings } from '../../hooks/useSettings';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { RefreshCw, ChevronLeft, ChevronRight, ArrowUpCircle, ArrowDownCircle, RotateCcw, Gift, Coins, Filter } from 'lucide-react';

const CreditLedgerSection = () => {
  const { settings, safeSettings } = useSettings();
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterType, setFilterType] = useState('');
  const pageSize = 15;

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  const authHeader = () => {
    const loginAsToken = sessionStorage.getItem('loginAsToken');
    const token = loginAsToken || localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const getTimeZone = () => {
    return (safeSettings && safeSettings.timezone) || (settings && settings.timezone) || 'UTC';
  };

  const formatDateTimeTZ = (dateString) => {
    if (!dateString) return '-';
    const tz = getTimeZone();
    const d = new Date(dateString);
    const day = new Intl.DateTimeFormat('en-GB', { timeZone: tz, day: '2-digit' }).format(d);
    const month = new Intl.DateTimeFormat('en-GB', { timeZone: tz, month: '2-digit' }).format(d);
    const year = new Intl.DateTimeFormat('en-GB', { timeZone: tz, year: 'numeric' }).format(d);
    const time = new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
    return `${day}/${month}/${year} ${time}`;
  };

  const fetchSummary = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/credit-summary`, { headers: authHeader() });
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Error fetching summary:', err);
    }
  };

  const fetchTransactions = async (pageNum = 1, txType = '') => {
    try {
      setLoading(true);
      setError('');

      let url = `${API_BASE_URL}/api/user/credit-ledger?page=${pageNum}&page_size=${pageSize}`;
      if (txType) {
        url += `&transaction_type=${txType}`;
      }

      const response = await fetch(url, { headers: authHeader() });
      if (!response.ok) {
        throw new Error('Failed to fetch credit ledger');
      }
      const data = await response.json();
      setTransactions(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setPage(data.page || 1);
    } catch (err) {
      console.error('Error fetching credit ledger:', err);
      setError('Failed to load credit ledger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchTransactions(1, filterType);
  }, []);

  const handleFilterChange = (type) => {
    setFilterType(type);
    setPage(1);
    fetchTransactions(1, type);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchTransactions(newPage, filterType);
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'credit_deduction':
        return <ArrowDownCircle size={16} color="#ef4444" />;
      case 'credit_refund':
        return <RotateCcw size={16} color="#22c55e" />;
      case 'credit_grant':
        return <Gift size={16} color="#8b5cf6" />;
      case 'plan_assignment':
        return <ArrowUpCircle size={16} color="#3b82f6" />;
      default:
        return <Coins size={16} color="#6b7280" />;
    }
  };

  const getTransactionLabel = (type) => {
    switch (type) {
      case 'credit_deduction':
        return 'Deduction';
      case 'credit_refund':
        return 'Refund';
      case 'credit_grant':
        return 'Credit Grant';
      case 'plan_assignment':
        return 'Plan Credits';
      default:
        return type;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'credit_deduction':
        return { bg: 'var(--color-danger-bg)', text: 'var(--color-danger-fg)', border: 'var(--color-danger-border)' };
      case 'credit_refund':
        return { bg: 'var(--color-success-bg)', text: '#166534', border: 'var(--color-success-border)' };
      case 'credit_grant':
        return { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe' };
      case 'plan_assignment':
        return { bg: 'var(--color-info-bg)', text: 'var(--color-info-fg)', border: 'var(--color-info-border)' };
      default:
        return { bg: 'var(--color-bg-subtle)', text: 'var(--color-text-secondary)', border: 'var(--color-border)' };
    }
  };

  const formatAmount = (amount) => {
    if (amount > 0) {
      return `+${amount.toLocaleString()}`;
    }
    return amount.toLocaleString();
  };

  return (
    <div>
      {/* Summary Cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
          <div style={{ background: 'var(--color-info-bg)', border: '1px solid var(--color-info-border)', borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--color-info-fg)', fontWeight: 600, marginBottom: 4 }}>Current Balance</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-info-hover)' }}>{summary.current_balance?.toLocaleString() || 0}</div>
          </div>
          <div style={{ background: 'var(--color-success-bg)', border: '1px solid var(--color-success-border)', borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 12, color: '#166534', fontWeight: 600, marginBottom: 4 }}>Total Granted</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-success-fg)' }}>{summary.total_granted?.toLocaleString() || 0}</div>
          </div>
          <div style={{ background: 'var(--color-danger-bg)', border: '1px solid var(--color-danger-border)', borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--color-danger-fg)', fontWeight: 600, marginBottom: 4 }}>Total Used</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-danger-hover)' }}>{summary.total_used?.toLocaleString() || 0}</div>
          </div>
          <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 12, color: '#5b21b6', fontWeight: 600, marginBottom: 4 }}>Total Refunded</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#7c3aed' }}>{summary.total_refunded?.toLocaleString() || 0}</div>
          </div>
        </div>
      )}

      {/* Filter and Refresh */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Filter size={16} color="#6b7280" />
          <select
            value={filterType}
            onChange={(e) => handleFilterChange(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 14, background: '#fff' }}
          >
            <option value="">All Transactions</option>
            <option value="credit_deduction">Deductions</option>
            <option value="credit_refund">Refunds</option>
            <option value="credit_grant">Credit Grants</option>
            <option value="plan_assignment">Plan Credits</option>
          </select>
        </div>
        <Button variant="outline" onClick={() => { fetchSummary(); fetchTransactions(page, filterType); }}>
          <RefreshCw size={14} style={{ marginRight: 6 }} />
          Refresh
        </Button>
      </div>

      {/* Transactions Table */}
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
          <RefreshCw size={28} style={{ animation: 'spin 1s linear infinite', color: '#7c3aed' }} />
        </div>
      ) : error ? (
        <Card style={{ padding: 24, textAlign: 'center' }}>
          <div style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{error}</div>
          <Button onClick={() => fetchTransactions(page, filterType)}>
            <RefreshCw size={14} style={{ marginRight: 6 }} />
            Try again
          </Button>
        </Card>
      ) : transactions.length === 0 ? (
        <Card style={{ padding: 32, textAlign: 'center' }}>
          <Coins size={40} color="#d1d5db" style={{ marginBottom: 12 }} />
          <div style={{ color: 'var(--color-text-muted)' }}>No transactions found</div>
        </Card>
      ) : (
        <>
          <div style={{ border: '1px solid var(--color-border)', borderRadius: 12, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '150px 120px 90px 90px 1fr',
              gap: '12px',
              background: 'var(--color-bg-subtle)',
              borderBottom: '1px solid var(--color-border)',
              padding: '12px 16px',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--color-text-muted)'
            }}>
              <div>Date</div>
              <div>Type</div>
              <div style={{ textAlign: 'right' }}>Amount</div>
              <div style={{ textAlign: 'right', paddingRight: 8 }}>Balance</div>
              <div style={{ paddingLeft: 12, borderLeft: '1px solid var(--color-border)' }}>Description</div>
            </div>

            {/* Rows */}
            {transactions.map((tx, idx) => {
              const colors = getTransactionColor(tx.transaction_type);
              return (
                <div
                  key={tx.id || idx}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '150px 120px 90px 90px 1fr',
                    gap: '12px',
                    padding: '12px 16px',
                    borderBottom: idx < transactions.length - 1 ? '1px solid var(--color-bg-inset)' : 'none',
                    alignItems: 'center',
                    background: '#fff'
                  }}
                >
                  <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{formatDateTimeTZ(tx.timestamp)}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {getTransactionIcon(tx.transaction_type)}
                    <span style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '3px 8px',
                      borderRadius: 12,
                      background: colors.bg,
                      color: colors.text,
                      border: `1px solid ${colors.border}`
                    }}>
                      {getTransactionLabel(tx.transaction_type)}
                    </span>
                  </div>
                  <div style={{
                    textAlign: 'right',
                    fontSize: 14,
                    fontWeight: 600,
                    color: tx.amount > 0 ? '#16a34a' : 'var(--color-danger-hover)'
                  }}>
                    {formatAmount(tx.amount)}
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 13, color: 'var(--color-text-muted)', paddingRight: 8 }}>
                    {tx.balance_after?.toLocaleString() || '-'}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingLeft: 12, borderLeft: '1px solid var(--color-bg-inset)' }} title={tx.reason}>
                    {tx.reason}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
              Page {page} of {totalPages} ({total} transactions)
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="outline"
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
              >
                <ChevronLeft size={14} />
                Prev
              </Button>
              <Button
                variant="outline"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages}
              >
                Next
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Spin animation style */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CreditLedgerSection;
